import type { AgentChatMessage } from '~/api/agentChat'

/**
 * Matches one-word content ending with "..." or Unicode ellipsis (e.g. "Searching...", "Analyzing…").
 * Used for both stored messages (partition) and streaming (trailing-segment detection).
 */
const STREAMING_INTERMEDIATE_PATTERN = /^\S+(\.\.\.|…)\s*$/

export const isStreamingIntermediateMessage = (
  message: AgentChatMessage,
): boolean => {
  const content =
    typeof message.content === 'string' ? message.content.trim() : ''
  return content.length > 0 && STREAMING_INTERMEDIATE_PATTERN.test(content)
}

const ELLIPSIS_ASCII = '...'
const ELLIPSIS_UNICODE = '…'

const isWhitespace = (c: string): boolean =>
  c === ' ' || c === '\t' || c === '\n' || c === '\r'

/**
 * Returns the trailing one-word+ellipsis segment from a streaming buffer, or null.
 * Only treats as intermediate when the entire buffer is that one word (e.g. "Searching...").
 */
export const getTrailingIntermediateSegment = (
  buffer: string,
): {
  length: number
  segment: string
} | null => {
  const trimmed = buffer.trimEnd()
  const ellipsis = trimmed.endsWith(ELLIPSIS_ASCII)
    ? ELLIPSIS_ASCII
    : trimmed.endsWith(ELLIPSIS_UNICODE)
      ? ELLIPSIS_UNICODE
      : null
  if (ellipsis === null) return null

  const ellipsisStart = trimmed.length - ellipsis.length
  let startIndex = ellipsisStart
  while (startIndex > 0 && !isWhitespace(trimmed[startIndex - 1])) {
    startIndex--
  }
  const segment = trimmed.slice(startIndex).trim()
  const length = buffer.length - startIndex
  const remainder = trimmed.slice(0, startIndex).trim()
  if (remainder.length > 0) return null
  return { segment, length }
}

export interface PartitionedAgentMessages {
  finalMessages: AgentChatMessage[]
  intermediateSegments: string[]
}

/**
 * Filters agent messages by content: hide only one-word "Thinking..." style;
 * show every message that does not match (final and any full-sentence intermediate).
 * Per-message rule; no structural markers used.
 */
export const partitionAgentMessages = (
  agentMessages: AgentChatMessage[],
): PartitionedAgentMessages => {
  const oneWord = agentMessages.filter(isStreamingIntermediateMessage)
  const finalMessages = agentMessages.filter(
    (m) => !isStreamingIntermediateMessage(m),
  )
  return {
    intermediateSegments: oneWord.map((m) => String(m.content ?? '')),
    finalMessages,
  }
}
